<template>
  <div class="row f-row h-100">
    <div
      class="col col-12 text-center progress-div"
      :style="'top: ' + progressPosition + '%!important;'"
      v-if="showLoading"
    >
      <QSLoading
        :isLoading="showLoadingSpot"
        :size="40"
        :colorTheme="colorTheme"
      />
    </div>
    <div
      class="col col-12 text-center"
      v-else-if="histData.length === 0 && fwdData.length === 0"
      style="position: absolute; left: 0; top: 27.5%; z-index: 10000000;"
    >
      No Data Available
    </div>
    <div class="col col-5" style="height:100%; width:100%">
      <div class="h-100 d-flex flex-column" :id="div1Name" style="width: 100%;">
        <svg :id="svg1Name"></svg>
      </div>
    </div>
    <div class="col col-7" style="height:100%; width:100%">
      <div class="h-100 d-flex flex-column" :id="div2Name" style="width: 100%;">
        <svg :id="svg2Name"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import {
  colorExpo,
  colorHedge,
  colorHedgeAlpha3,
  currencyFormat,
  grayLightBackground,
} from "@/_core/_helpers/_parametric_tools";
import { D3Chart } from "@/_core/charts/D3Chart";
import { D3HoverMktHist } from "@/_core/charts/D3ChartMarketshover";
import { format } from "d3";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "CPOComparisonChart",
  components: {
    QSLoading,
  },
  props: {
    allData: { required: true, default: () => [] },
    allData2: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    comp: { required: true },
    compRott: { required: true },
    div1Name: { required: true },
    div2Name: { required: true },
    fwdData: { required: true, default: () => [] },
    fwdData2: { required: true, default: () => [] },
    histData: { required: true, default: () => [] },
    histData2: { required: true, default: () => [] },
    histData2Monthly: { required: true, default: () => [] },
    interval: { required: true },
    monthData: { required: true, default: () => [] },
    monthData2: { required: true, default: () => [] },
    progressPosition: { required: true },
    renderChartsSignal: { required: true, default: 0 },
    showLoading: { required: true, type: Boolean, default: false },
    svg1Name: { required: true },
    svg2Name: { required: true },
    // ============ Data Classes ===============
    processData: { required: true },
  },
  data() {
    return {
      D3ChartFwd: null,
      D3ChartHist: null,
      D3HoverMktHist: null,
      D3HoverMktHistb: null,
      minMaxYAxis: [-Infinity, +Infinity],
      monthDate: "%b%y",
      svgNodeFwd: null,
      svgNodeHist: null,
    };
  },
  created() {
    this.D3HoverMktHist = new D3HoverMktHist();
    this.D3HoverMktHistb = new D3HoverMktHist();
    this.D3ChartHist = new D3Chart(this.div1Name, this.svg1Name);
    this.D3ChartFwd = new D3Chart(this.div2Name, this.svg2Name);
  },
  mounted() {
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
    this.callWidth();
  },
  watch: {
    colorTheme() {
      this.callWidth();
    },
    showLoading(val) {
      if (val === false) {
        this.renderCharts();
      }
    },
    renderChartsSignal() {
      if (this.histData.length > 0 && this.fwdData.length > 0) {
        this.renderCharts();
      }
    },
  },
  methods: {
    callWidth() {
      if (this.histData.length > 0) {
        this.renderCharts();
      }
    },
    renderCharts() {
      // === historical
      this.svgNodeHist = this.D3ChartHist.setSVG(
        0.5,
        5,
        0.5,
        2,
        0.5,
        25,
        0.5,
        5,
        this.colorTheme,
      );
      this.minMaxYAxis = this.processData.minMax(this.histData, "high", "low");
      this.minMaxYAxis = this.processData.minMax(
        this.histData2,
        "close",
        "close",
        this.minMaxYAxis,
      );
      this.minMaxYAxis = this.processData.minMax(
        this.fwdData,
        "close",
        "close",
        this.minMaxYAxis,
      );
      this.minMaxYAxis = this.processData.minMax(
        this.fwdData2,
        "close",
        "close",
        this.minMaxYAxis,
      );
      if (this.monthData.length > 0) {
        this.minMaxYAxis = this.processData.minMax(
          this.monthData,
          "close",
          "close",
          this.minMaxYAxis,
        );
      }
      let del = 0.1 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      this.minMaxYAxis = [
        this.minMaxYAxis[0] - del,
        this.minMaxYAxis[1] + 2 * del,
      ];
      this.svgNodeHist = this.D3ChartHist.setAxis(
        this.svgNodeHist,
        this.histData,
        this.minMaxYAxis,
        "%b %y",
        60,
        8,
        false,
        [],
        true,
        true,
        true,
        format(currencyFormat),
        true,
      );
      // === add historical lines and bars
      this.D3ChartHist.bars(
        this.svgNodeHist,
        this.histData,
        0.85,
        "ROTT",
        colorExpo(this.colorTheme),
        "rgba(0,0,0,0)",
        "date",
        "close",
        true,
        false,
        false,
        2,
        1,
        true,
        "id",
      );
      this.D3ChartHist.bars(
        this.svgNodeHist,
        this.histData2Monthly,
        0.85,
        "MAL",
        colorHedge(this.colorTheme),
        "rgba(0,0,0,0)",
        "date",
        "close",
        true,
        false,
        false,
        2,
        1,
        true,
        "id",
      );

      let _this = this;
      let concatenated = this.histData2
        .filter((d) => d.date > _this.histData[0].date)
        .concat(this.monthData2);
      let ran = this.histData.map((d) => d.date);

      this.svgNodeHist = this.D3ChartHist.line(
        this.svgNodeHist,
        concatenated,
        colorHedgeAlpha3(this.colorTheme),
        1,
        "close",
        true,
        1 / 22,
        ran,
      );

      // === forwards
      this.svgNodeFwd = this.D3ChartFwd.setSVG(
        0.5,
        5,
        0.5,
        70,
        0.5,
        25,
        0.5,
        0,
        this.colorTheme,
      );
      this.svgNodeFwd = this.D3ChartFwd.setAxis(
        this.svgNodeFwd,
        this.fwdData,
        this.minMaxYAxis,
        this.monthDate,
        60,
        8,
        false,
        [],
        true,
        true,
        true,
        format(currencyFormat),
        false,
      );

      // Divider - Vertical Line
      this.svgNodeFwd
        .append("line")
        .attr("stroke-width", 1)
        .attr("stroke", grayLightBackground(this.colorTheme))
        .attr("x1", this.svgNodeFwd.margin.left)
        .attr("y1", 0)
        .attr("x1", this.svgNodeFwd.margin.left)
        .attr("y2", this.svgNodeFwd.height);

      // === add fwd rates
      this.D3ChartFwd.bars(
        this.svgNodeFwd,
        this.fwdData,
        0.8,
        "RATEb",
        colorExpo(this.colorTheme),
        "rgba(0,0,0,0)",
        "date",
        "close",
        true,
        false,
        false,
        2,
        1,
      );
      this.D3ChartFwd.bars(
        this.svgNodeFwd,
        this.fwdData2,
        0.8,
        "MALb",
        colorHedge(this.colorTheme),
        "rgba(0,0,0,0)",
        "date",
        "close",
        true,
        false,
        false,
        2,
        1,
      );

      // === custom hover effects and info
      this.D3HoverMktHist.hoverFctComp(
        this.svgNodeHist,
        this.histData,
        this.histData2Monthly,
        "RATE",
        format(currencyFormat),
        "%b %y",
        "MONTH",
        format(currencyFormat),
        this.svgNodeHist,
        this.svgNodeFwd,
        1,
        this.allData,
        this.allData2,
        this.comp,
        this.compRott,
        this.colorTheme,
      );
      this.D3HoverMktHistb.hoverFctComp(
        this.svgNodeFwd,
        this.fwdData,
        this.fwdData2,
        "RATEb",
        format(currencyFormat),
        "%b %y",
        "MONTH",
        format(currencyFormat),
        this.svgNodeHist,
        this.svgNodeFwd,
        2,
        this.allData,
        this.allData2,
        this.comp,
        this.compRott,
        this.colorTheme,
      );
    },
  },
};
</script>

<style scoped></style>
